import React from 'react';
import { Brain, Plane, Monitor, Activity, Cpu, LineChart, Zap } from 'lucide-react';
import { omSolutions } from '../mock/mockData';

const iconMap = {
  Brain,
  Plane,
  Monitor,
  Activity,
  Cpu,
  LineChart,
  Zap
};

const OMDigital = () => {
  return (
    <div className="min-h-screen bg-[#081221]">
      {/* Hero Section - Dark Tech Style */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d63ce]/20 to-[#18a06b]/10" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
            backgroundSize: '4px 4px'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-[#18a06b]/20 backdrop-blur-sm rounded-full text-[#18a06b] font-semibold mb-6">
            DIGITAL SOLUTIONS
          </div>
          <h1 className="font-serif text-6xl md:text-7xl font-bold text-white mb-6">
            O&M e Soluzioni Digitali
          </h1>
          <p className="text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
            Tecnologie avanzate per la gestione operativa di impianti fotovoltaici utility-scale
          </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 bg-[#081221]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {omSolutions.features.map((solution) => {
              const IconComponent = iconMap[solution.icon];
              return (
                <div
                  key={solution.id}
                  className="group relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#18a06b]/50 transition-all transform hover:-translate-y-2 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0d63ce]/0 to-[#18a06b]/0 group-hover:from-[#0d63ce]/10 group-hover:to-[#18a06b]/10 transition-all" />
                  
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <IconComponent className="text-white" size={32} />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-white mb-4">
                      {solution.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">{solution.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Monitoring Systems */}
      <section className="py-24 bg-gradient-to-br from-[#0d63ce]/20 to-[#18a06b]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#18a06b]/30 backdrop-blur-sm rounded-full text-[#18a06b] font-semibold mb-6">
                SISTEMI DI MONITORAGGIO
              </div>
              <h2 className="font-serif text-5xl font-bold text-white mb-6">
                Sistemi di Monitoraggio Evoluti
              </h2>
              <p className="text-xl text-white/80 leading-relaxed mb-8">
                Integrazione completa tra attività on-site e soluzioni tecnologiche per l'eccellenza operativa e la massimizzazione delle performance.
              </p>
              <ul className="space-y-4">
                {[
                  'Monitoraggio real-time di produzione e performance',
                  'Dashboard personalizzate e reportistica automatica',
                  'Integrazione con SCADA e inverter',
                  'Alert automatici e gestione ticket',
                  'Analytics avanzati e benchmarking',
                  'Pronto intervento H24 per emergenze'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#18a06b] rounded-full" />
                    <span className="text-white/80 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <video
                  src="/new_video.mp4"
                  alt="Monitoring Dashboard"
                  className="rounded-lg shadow-2xl"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-2xl opacity-20 -z-10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Drone Inspections */}
      <section className="py-24 bg-[#081221]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <img
                  src="/photo7.jpeg"
                  alt="Drone Inspection"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
            <div>
              <div className="inline-block px-4 py-2 bg-[#0d63ce]/30 backdrop-blur-sm rounded-full text-[#0d63ce] font-semibold mb-6">
                TECNOLOGIA AVANZATA
              </div>
              <h2 className="font-serif text-5xl font-bold text-white mb-6">
                Ispezioni con Droni
              </h2>
              <p className="text-xl text-white/80 leading-relaxed mb-8">
                Utilizziamo droni dotati di termocamere ad alta risoluzione per rilevare anomalie e
                guasti su larga scala in tempi ridotti.
              </p>
              <div className="space-y-6">
                {[
                  {
                    title: 'Termografia Aerea',
                    description: 'Rilevamento di hot spot e celle danneggiate'
                  },
                  {
                    title: 'Analisi RGB',
                    description: 'Identificazione di difetti visivi e sporcizia'
                  },
                  {
                    title: 'Report Automatici',
                    description: 'Elaborazione dati e generazione report automatici'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Plane className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-serif text-xl font-bold text-white mb-2">
                        {item.title}
                      </h4>
                      <p className="text-white/70">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-24 bg-[#081221]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-5xl font-bold text-white mb-16">
            Risultati Misurabili
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '50+', label: 'Impianti in gestione O&M' },
              { value: '99.5%', label: 'Uptime medio' },
              { value: '-25%', label: 'Riduzione downtime' },
              { value: '+12%', label: 'Ottimizzazione performance' }
            ].map((stat, index) => (
              <div key={index} className="p-8">
                <div className="text-5xl font-bold text-[#18a06b] mb-4">{stat.value}</div>
                <div className="text-white/80 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default OMDigital;
